import { Tooltip } from "./Tooltip.tsx";

export type StatusMarkProps = {
  emoji: string;
  /** The status text ("" when emoji only). */
  text: string;
  /** "Until 7:59 PM", already translated; absent when it never clears. */
  until?: string;
  /** Accessible name of the emoji ("Status: Out sick"), already translated. */
  label: string;
  /** sm 14px (message headers), md 16px (DM header); default 15px (rows). */
  size?: "sm" | "md";
  /** Hover / focus shows the status card (default true). */
  tooltip?: boolean;
  testID?: string;
};

/**
 * A person's status emoji right after their name: never truncated, never shrinks. Hover (after ~300ms) shows a card
 * with the emoji, the text (2 lines at most) and "Until …". Native: a Text emoji after the name; touch shows the full
 * status in the profile sheet (long-press) since there is no hover.
 */
export function StatusMark({ emoji, text, until, label, size, tooltip = true, testID }: StatusMarkProps) {
  const mark = (
    <span className={size === undefined ? "b-status-emoji" : `b-status-emoji b-status-emoji-${size}`} role="img" aria-label={label} data-testid={testID}>
      {emoji}
    </span>
  );
  if (!tooltip) return mark;
  return (
    <Tooltip label={text === "" ? emoji : text} content={<StatusCard emoji={emoji} text={text} until={until} />} delay={300}>
      {mark}
    </Tooltip>
  );
}

/** The status card body: emoji, text (2 lines at most) and "Until …" (none when it never clears). */
export function StatusCard({ emoji, text, until }: { emoji: string; text: string; until?: string }) {
  return (
    <span className="b-status-card">
      <span className="b-status-card-emoji" aria-hidden>
        {emoji}
      </span>
      {text === "" ? null : <span className="b-status-card-text">{text}</span>}
      {until === undefined ? null : <span className="b-status-card-until">{until}</span>}
    </span>
  );
}
